import { telLink, waLink, smsLink } from '@/lib/phone';

type Channel = 'call' | 'whatsapp' | 'sms' | 'email';

interface ContactButtonsProps {
  phone?: string | null;
  email?: string | null;
  clientId: string;
  /** Name shown in the contact-log prompt */
  contactName?: string;
  routeStepId?: string;
  onContactLogged?: () => void;
  /** Compact = icon-only (mobile / tight spaces) */
  compact?: boolean;
}

const PENDING_KEY = 'pendingContactLog';

function setPendingLog(data: {
  clientId: string;
  routeStepId?: string;
  channel: Channel;
  contactName?: string;
  timestamp: number;
}) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
}

function ContactLink({
  href,
  label,
  icon,
  compact,
  channel,
  clientId,
  routeStepId,
  contactName,
}: {
  href: string;
  label: string;
  icon: string;
  compact?: boolean;
  channel: Channel;
  clientId: string;
  routeStepId?: string;
  contactName?: string;
}) {
  const handleClick = () => {
    setPendingLog({
      clientId,
      routeStepId,
      channel,
      contactName,
      timestamp: Date.now(),
    });
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--glass-border)] bg-white/50 text-[var(--text-secondary)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 transition-all text-xs font-medium min-w-[44px] min-h-[44px] sm:min-h-[auto] sm:min-w-[auto] sm:py-1 justify-center"
      rel="noopener noreferrer"
    >
      <span className="text-sm">{icon}</span>
      {!compact && <span className="hidden sm:inline">{label}</span>}
    </a>
  );
}

export function ContactButtons({
  phone,
  email,
  clientId,
  contactName,
  routeStepId,
  compact,
}: ContactButtonsProps) {
  if (!phone && !email) return null;

  const shared = { clientId, routeStepId, contactName, compact };

  return (
    <div className="inline-flex items-center gap-1">
      {phone && (
        <>
          <ContactLink href={telLink(phone)} label="Call" icon="📞" channel="call" {...shared} />
          <ContactLink href={waLink(phone)} label="WhatsApp" icon="💬" channel="whatsapp" {...shared} />
          <ContactLink href={smsLink(phone)} label="Text" icon="📱" channel="sms" {...shared} />
        </>
      )}
      {email && (
        <ContactLink href={`mailto:${email}`} label="Email" icon="📧" channel="email" {...shared} />
      )}
    </div>
  );
}

export { PENDING_KEY };
export type { Channel };
